import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Eye, Search, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorOrders } from "@/hooks/useVendor";
import { formatPrice, formatDate, ORDER_STATUS, PAYMENT_STATUS } from "@/lib/format";

export default function VendorOrders() {
  const { data: orders, isLoading } = useVendorOrders();
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const list = (orders || []).filter((o) => !q ||
    [o.order_number, o.customer?.name, o.customer?.email, ...o.items.map((i) => i.name)]
      .join(" ").toLowerCase().includes(q.toLowerCase()));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/vendor/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "mes-commandes.csv"; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch (e) { toast.error(apiError(e, "Export impossible")); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-orders">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Mes commandes</h1>
          <p className="mt-1 text-sm text-slate-500">Gérez vos commandes de A à Z : paiement, statut, livraison — sans intervention de l'administrateur.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." data-testid="vorders-search"
              className="h-10 w-52 rounded-lg border border-line pl-9 pr-3 text-sm focus:border-brand focus:outline-none" />
          </span>
          <button onClick={exportCsv} disabled={exporting || !list.length} data-testid="vorders-export"
            className="flex h-10 items-center gap-2 rounded-lg border border-line px-4 text-xs font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-50">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exporter CSV
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !list.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <ShoppingBag className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune commande pour le moment.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="p-4">Commande</th><th className="p-4">Client</th><th className="p-4">Contenu</th><th className="p-4">Montant</th><th className="p-4">Paiement</th><th className="p-4">Statut</th><th className="p-4 text-right">Action</th>
            </tr></thead>
            <tbody className="divide-y divide-line">
              {list.map((o) => {
                const st = ORDER_STATUS[o.status] || {};
                const ps = PAYMENT_STATUS[o.payment_status] || {};
                return (
                  <tr key={o.id} data-testid={`vorder-${o.id}`}>
                    <td className="p-4"><span className="font-bold text-ink">{o.order_number}</span><br /><span className="text-xs text-slate-400">{formatDate(o.created_at)}</span></td>
                    <td className="p-4 text-ink">{o.customer?.name || "—"}</td>
                    <td className="max-w-[220px] p-4 text-xs text-slate-600">{o.items.map((i) => i.name).join(", ")}</td>
                    <td className="p-4 font-bold text-ink">{formatPrice(o.total)}</td>
                    <td className="p-4"><span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${ps.className || "bg-slate-100 text-slate-600"}`}>{ps.label || o.payment_status}</span></td>
                    <td className="p-4"><span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${st.className || "bg-slate-100 text-slate-600"}`}>{st.label || o.status}</span></td>
                    <td className="p-4 text-right">
                      <Link to={`/vendeur/commandes/${o.id}`} data-testid={`vorder-manage-${o.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-bold text-white hover:bg-brand">
                        <Eye className="h-3.5 w-3.5" /> Gérer
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
